import type { BillingCycle, SubscriptionPlan } from "./pricing";

export type PurchaseKind = "subscription" | "payg" | "legacy_pack";
export type PromotionStatus = "upcoming" | "active" | "ended";

export type Promotion = {
  id: string;
  publicName: string;
  promoCode: string;
  discountType: "percentage";
  discountPercent: number;
  startsAt: number;
  endsAt: number;
  eligiblePurchaseKinds: readonly PurchaseKind[];
  eligiblePlans: readonly SubscriptionPlan[];
  maxApprovedRedemptionsPerUser: number;
};

// Morocco observes UTC+1 on both campaign boundary dates in 2026.
export const BACK_TO_SCHOOL_2026 = {
  id: "back_to_school_2026",
  publicName: "Back to School 2026",
  promoCode: "SCHOOL26",
  discountType: "percentage",
  discountPercent: 50,
  startsAt: Date.parse("2026-08-24T23:00:00.000Z"),
  endsAt: Date.parse("2026-09-20T22:59:59.999Z"),
  eligiblePurchaseKinds: ["subscription"],
  eligiblePlans: ["starter", "pro"],
  maxApprovedRedemptionsPerUser: 1,
} as const satisfies Promotion;

export const PROMOTIONS: readonly Promotion[] = [BACK_TO_SCHOOL_2026];

export function normalizePromoCode(value: string): string {
  return value.trim().toUpperCase();
}

export function findPromotionByCode(value: string): Promotion | null {
  const normalized = normalizePromoCode(value);
  return PROMOTIONS.find((promotion) => promotion.promoCode === normalized) ?? null;
}

export function promotionStatusAt(
  promotion: Promotion,
  now: number,
): PromotionStatus {
  if (now < promotion.startsAt) return "upcoming";
  if (now > promotion.endsAt) return "ended";
  return "active";
}

export function isPromotionActive(promotion: Promotion, now: number): boolean {
  return promotionStatusAt(promotion, now) === "active";
}

export function isPromotionActiveAfterLaunch(
  promotion: Promotion,
  launchedAt: number | null,
  now: number,
): boolean {
  return launchedAt !== null && launchedAt <= now && now <= promotion.endsAt;
}

export function isPromotionEligible(
  promotion: Promotion,
  purchaseKind: PurchaseKind,
  plan?: SubscriptionPlan,
  cycle?: BillingCycle,
): boolean {
  return (
    promotion.eligiblePurchaseKinds.includes(purchaseKind) &&
    purchaseKind === "subscription" &&
    plan !== undefined &&
    promotion.eligiblePlans.includes(plan) &&
    (cycle === "semester" || cycle === "yearly")
  );
}

export function madToCentimes(amountMad: number): number {
  if (!Number.isFinite(amountMad) || amountMad < 0) {
    throw new Error("Invalid MAD amount");
  }
  return Math.round(amountMad * 100);
}

export function centimesToMad(centimes: number): number {
  if (!Number.isSafeInteger(centimes) || centimes < 0) {
    throw new Error("Invalid centime amount");
  }
  return centimes / 100;
}

export function calculateDiscountAmountCentimes(
  originalCentimes: number,
  discountPercent: number,
): number {
  if (!Number.isSafeInteger(originalCentimes) || originalCentimes < 0) {
    throw new Error("Invalid centime amount");
  }
  if (!Number.isInteger(discountPercent) || discountPercent < 0 || discountPercent > 100) {
    throw new Error("Invalid discount percentage");
  }
  return Math.round((originalCentimes * discountPercent) / 100);
}

export function calculateDiscountAmount(
  originalAmountMad: number,
  promotion: Promotion,
): number {
  return centimesToMad(
    calculateDiscountAmountCentimes(
      madToCentimes(originalAmountMad),
      promotion.discountPercent,
    ),
  );
}

export function calculatePromotionalPrice(
  originalAmountMad: number,
  promotion: Promotion,
): number {
  const originalCentimes = madToCentimes(originalAmountMad);
  const discountCentimes = calculateDiscountAmountCentimes(
    originalCentimes,
    promotion.discountPercent,
  );
  return centimesToMad(originalCentimes - discountCentimes);
}

export function hasReachedPromotionRedemptionLimit(
  promotion: Promotion,
  approvedRedemptionCount: number,
): boolean {
  return approvedRedemptionCount >= promotion.maxApprovedRedemptionsPerUser;
}

export function isPromotionPricingConsistent({
  promotion,
  purchaseKind,
  plan,
  cycle,
  originalAmount,
  discountAmount,
  finalAmount,
}: {
  promotion: Promotion;
  purchaseKind: PurchaseKind;
  plan: SubscriptionPlan;
  cycle: BillingCycle;
  originalAmount: number;
  discountAmount: number;
  finalAmount: number;
}): boolean {
  return (
    isPromotionEligible(promotion, purchaseKind, plan, cycle) &&
    calculateDiscountAmount(originalAmount, promotion) === discountAmount &&
    calculatePromotionalPrice(originalAmount, promotion) === finalAmount
  );
}
